import React, { useEffect, useMemo, useState } from "react";
import useStyles from "./styles";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  Button,
  Slider,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import CeroInput from "../../../../components/CeroInput";
import CeroPhoneInput from "../../../../components/CeroPhoneInput";
import LinearProgress from "@mui/material/LinearProgress";
import OversightFormTable from "./CsrdTables/csrdtable1";
import DueDiligenceTable from "./CsrdTables/csrdtable2";
import PolicyTable from "./CsrdTables/csrdtable3";
import ResourceAllocationTable from "./CsrdTables/csrdtable4";
import SustainabilityTargetsTable from "./CsrdTables/csrdtable5";
import {
  getStandardsQuestionnaireAnswers,
  saveStandardsQuestionnaire,
} from "../../../../redux/actions";
import { getStandardsQuestionnaire } from "../../../../redux/actions";
import { useLocation } from "react-router-dom";

function CsrdQuestionnaire({
  handleCheckboxChange,
  handleSingleSelect,
  handleTextInputChange,
  textAnswers,
  selectedValues,
  oversightFormTable,
  setOversightFormTable,
  dueDiligenceData,
  setDueDiligenceData,
  policyData,
  setPolicyData,
  targetsData,
  setTargetsData,
  resourceData,
  setResourceData,
  assessmentYear,
  selectedCountry,
  handleDateInputChange,
  dateAnswers,
  setHideSaveSubmitButtons,
}) {
  const classes = useStyles();
  const [currentPage, setCurrentPage] = useState(1);
  const SLIDER_WIDTH = 215;
  const location = useLocation();

  const csrdQuestions = useSelector((state) => {
    const data = state.emission.getStandardsQuestionnaire.data.questions_list;
    return Array.isArray(data) ? data : [];
  });

  const optionsById = useMemo(
    () =>
      Object.fromEntries(
        csrdQuestions.map((q) => [String(q.id), q.options || []])
      ),
    [csrdQuestions]
  );

  const questionnaireData = useSelector(
    (state) => state.emission.getStandardsQuestionnaire.data
  );
  const rawAnswerPayload = useSelector(
    (state) => state.assessment.getStandardsQuestionnaireAnswers?.data
  );

  const savedAnswers = useMemo(() => {
    if (Array.isArray(rawAnswerPayload?.answer)) {
      return rawAnswerPayload.answer; // Edit mode
    } else if (Array.isArray(rawAnswerPayload)) {
      return rawAnswerPayload;
    }
    return [];
  }, [rawAnswerPayload]);

  const fallbackData = useSelector(
    (state) => state.emission.getStandardsQuestionnaireHistory?.data
  );

  const [sliderAnswers, setSliderAnswers] = useState({});

  const [sliderPrefillReady, setSliderPrefillReady] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!Array.isArray(savedAnswers) || csrdQuestions.length === 0) {
      return;
    }

    const prefilled = {};
    const prefilledText = {};
    const preSelectedById = {};

    savedAnswers.forEach((ans) => {
      const id = String(ans.question_id || ans.id);

      if (ans.question_type === "slider" && Array.isArray(ans.answer)) {
        const sliderValue = ans.answer[0]?.value;
        if (sliderValue !== undefined && sliderValue !== null) {
          prefilled[id] = sliderValue;
        }
      }

      if (
        ans.question_type === "choose_from_option" &&
        typeof ans.answer === "string"
      ) {
        const rawId = ans.question_id ?? ans.id;
        const idStr = String(rawId); // for text keys
        const idForSelect =
          typeof rawId === "number" // for handlers
            ? rawId
            : Number(rawId) || rawId;

        const low = ans.answer.trim().toLowerCase();

        const needsExplain =
          low.startsWith("other") ||
          low.startsWith("yes (please specify") ||
          low.startsWith("yes, please specify");

        if (needsExplain) {
          prefilledText[`specify-${idStr}`] =
            ans.answer_text || ans.other || "";
        }

        const match =
          (optionsById[idStr] || []).find(
            (o) => o.trim().toLowerCase() === ans.answer.trim().toLowerCase()
          ) || ans.answer;

        if (typeof handleSingleSelect === "function") {
          handleSingleSelect(idForSelect, match);
        }
      }

      if (
        ans.question_type === "multiselect_check_box" &&
        Array.isArray(ans.answer)
      ) {
        const idStr = String(id);
        const optsForQ = optionsById[idStr] || [];

        const isCommitteesBlock =
          optsForQ.length === 4 &&
          [
            "Audit Committee: ",
            "Remuneration Committee: ",
            "Nomination Committee: ",
            "Other (Please specify):",
          ].every((lbl) =>
            optsForQ.some(
              (o) => o.trim().toLowerCase() === lbl.trim().toLowerCase()
            )
          );

        ans.answer.forEach((txt) => {
          const opt = String(txt || "");
          const lower = opt.trim().toLowerCase();

          if (isCommitteesBlock) {
            const m = opt.match(
              /^(Audit Committee: |Remuneration Committee: |Nomination Committee: |Other \(Please specify\):)\s*(.*)$/i
            );
            if (m) {
              const labelWithColon = m[1];
              const explanation = (m[2] || "").trim();
              prefilledText[`${idStr}-${labelWithColon}`] = explanation;

              const baseOpt =
                optsForQ.find(
                  (o) =>
                    o.trim().toLowerCase() ===
                    labelWithColon.trim().toLowerCase()
                ) || labelWithColon;

              (preSelectedById[idStr] ||= new Set()).add(baseOpt);
              return; // handled
            }
          }

          if (
            lower.includes("please specify") ||
            lower.includes("explain below") ||
            lower.includes("other entities")
          ) {
            const cleaned = opt
              .replace(
                /^(Other\s*\(Please\s*specify\)\s*:?\s*|Other entities in your value chain\s*\(explain below\)\s*:?\s*|.*?\(explain below\)\s*:?\s*)/i,
                ""
              )
              .trim();

            if (cleaned) {
              prefilledText[`other-${idStr}`] = cleaned;
            }

            const baseOpt =
              optsForQ.find((o) =>
                o.trim().toLowerCase().startsWith("other (please specify")
              ) ||
              optsForQ.find((o) => o.toLowerCase().includes("explain below")) ||
              optsForQ.find((o) =>
                o.toLowerCase().includes("other entities")
              ) ||
              "Other (Please specify):";

            (preSelectedById[idStr] ||= new Set()).add(baseOpt);
          }
        });
      }
    });
    Object.entries(prefilledText).forEach(([k, v]) => {
      if (typeof v === "string") {
        handleTextInputChange(k, v);
      }
    });

    Object.entries(preSelectedById).forEach(([qid, set]) => {
      Array.from(set).forEach((opt) => {
        const already =
          Array.isArray(selectedValues[qid]) &&
          selectedValues[qid].includes(opt);
        if (!already) {
          handleCheckboxChange({ target: { checked: true, value: opt } }, qid);
        }
      });
    });

    setSliderAnswers(prefilled);
    setSliderPrefillReady(true);
  }, [savedAnswers, csrdQuestions, questionnaireData]);

  const hasSliderQuestions = csrdQuestions.some(
    (q) => q.question_type === "slider"
  );

  useEffect(() => {
    const interval = setInterval(() => {
      document.querySelectorAll("button").forEach((btn) => {
        if (btn.innerText === "Save" || btn.innerText === "Submit") {
          btn.style.display = "none";
        }
      });
    }, 200);

    return () => clearInterval(interval);
  }, []);

  const questionDict = {};
  const subQuestionDict = {};
  const toRoman = (num) =>
    ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"][num - 1] ||
    num;

  csrdQuestions.forEach(
    ({
      main_section,
      section,
      sub_section,
      question,
      category,
      question_type,
      options,
      id,
      condition,
      question_id,
    }) => {
      if (!questionDict[main_section]) questionDict[main_section] = {};
      if (!questionDict[main_section][section])
        questionDict[main_section][section] = {};
      if (!questionDict[main_section][section][sub_section])
        questionDict[main_section][section][sub_section] = [];

      if (question_id !== "None") {
        if (!subQuestionDict[question_id]) subQuestionDict[question_id] = [];
        subQuestionDict[question_id].push({
          question,
          category,
          question_type,
          options,
          id,
          condition,
          question_id,
        });
      }

      questionDict[main_section][section][sub_section].push({
        question,
        category,
        question_type,
        options,
        id,
        condition,
        question_id,
      });
    }
  );
  const getDefaultTableRowsForCondition = (condition) => {
    switch (condition) {
      case "csrd_table_1":
        return [
          { aspect: "Strategy and business model", oversightForm: "" },
          { aspect: "Risk and opportunity assessment", oversightForm: "" },
          { aspect: "Policies, targets, and action plans", oversightForm: "" },
          { aspect: "Reporting and disclosures", oversightForm: "" },
        ];
      case "csrd_table_2":
        return [
          {
            coreElement:
              "Embedding due diligence in governance, strategy and business model",
            relatedDisclosure: "",
            coversPeople: "",
            coversEnvironment: "",
          },
          {
            coreElement:
              "Engaging with affected stakeholders in all key steps of the due diligence",
            relatedDisclosure: "",
            coversPeople: "",
            coversEnvironment: "",
          },
          {
            coreElement: "Identifying and assessing adverse impacts",
            relatedDisclosure: "",
            coversPeople: "",
            coversEnvironment: "",
          },
          {
            coreElement: "Taking actions to address those adverse impacts",
            relatedDisclosure: "",
            coversPeople: "",
            coversEnvironment: "",
          },
          {
            coreElement:
              "Tracking the effectiveness of these efforts and communicating",
            relatedDisclosure: "",
            coversPeople: "",
            coversEnvironment: "",
          },
        ];
      case "csrd_table_3":
        return [
          {
            policy: "",
            description: "",
            scope: "",
            accountablePerson: "",
            recognisedInstruments: "",
            availability: "",
          },
        ];
      case "csrd_table_4":
        return [
          {
            sustainabilityMatter: "",
            capex: "",
            opex: "",
            allocationYear: "",
            futureResources: "",
          },
        ];
      case "csrd_table_5":
        return [
          {
            sustainabilityMatter: "",
            targetDescription: "",
            targetType: "",
            targetYear: "",
            baselineYear: "",
          },
        ];
      default:
        return [];
    }
  };

  const collectAnswers = () => {
    const answer_list = [];

    csrdQuestions.forEach((q) => {
      const { id, question, question_type, condition } = q;

      if (question_type === "text" || question_type === "short_text") {
        const answerValue = (textAnswers[id] || "").toString().trim();
        answer_list.push({
          question_id: id,
          question_name: question,
          question_type,
          condition: condition || "",
          answer: answerValue,
        });
      }

      if (question_type === "choose_from_option") {
        const answerValue = (selectedValues[id] || "").toString().trim();
        const low = answerValue.toLowerCase();

        const answerObj = {
          question_id: id,
          question_name: question,
          question_type,
          condition: condition || "",
          answer: answerValue,
        };

        const needsExplain =
          low.startsWith("other") ||
          low.startsWith("yes (please specify") ||
          low.startsWith("yes, please specify");

        if (needsExplain) {
          const extra = textAnswers[`specify-${id}`] || "";
          answerObj.answer_text = extra;
          if (low.startsWith("other")) answerObj.other = extra;
        }

        answer_list.push(answerObj);
      }

      if (question_type === "multiselect_check_box") {
        let selected = selectedValues[id] || [];
        selected = [...new Set(selected)];

        const isCommitteesBlock =
          JSON.stringify(q.options) ===
          JSON.stringify([
            "Audit Committee: ",
            "Remuneration Committee: ",
            "Nomination Committee: ",
            "Other (Please specify):",
          ]);

        let answerValue;

        if (isCommitteesBlock) {
          answerValue = selected.map((option) => {
            const textKey = `${id}-${option}`;
            const extra = (textAnswers[textKey] || "").trim();

            return extra
              ? `${option}${extra.startsWith(":") ? "" : " "}${extra}`
              : option;
          });
        } else {
          answerValue = selected.map((option) => {
            const explanation = (textAnswers[`other-${id}`] || "").trim();
            const lower = option.toLowerCase();
            if (
              lower.includes("please specify") ||
              lower.includes("explain below") ||
              lower.includes("other entities")
            ) {
              return explanation ? `${option} ${explanation}` : option;
            }
            return option;
          });
        }

        answerValue = answerValue.filter((v) => String(v || "").trim() !== "");

        answer_list.push({
          question_id: id,
          question_name: question,
          question_type,
          condition: condition || "",
          answer: answerValue,
        });
      }

      if (question_type === "slider") {
        const sliderValue = sliderAnswers[id] || 3;
        answer_list.push({
          question_id: id,
          question_name: question,
          question_type,
          condition: condition || "",
          answer: [{ value: sliderValue }],
        });
      }

      if (question_type === "table") {
        let tableData = [];
        switch (condition) {
          case "csrd_table_1":
            tableData = oversightFormTable;
            break;
          case "csrd_table_2":
            tableData = dueDiligenceData;
            break;
          case "csrd_table_3":
            tableData = policyData;
            break;
          case "csrd_table_4":
            tableData = resourceData;
            break;
          case "csrd_table_5":
            tableData = targetsData;
            break;
          default:
            tableData = [];
        }

        answer_list.push({
          question_id: id,
          question_name: question,
          question_type,
          condition: condition || "",
          answer: (tableData.length > 0
            ? tableData
            : getDefaultTableRowsForCondition(condition)
          ).map((row) =>
            Object.keys(row).reduce(
              (acc, key) => ({ ...acc, [key]: row[key] || "" }),
              {}
            )
          ),
        });
      }
    });

    return answer_list;
  };

  const frameworkList = useSelector(
    (state) => state.listings.frameworkList?.data
  );

  const frameworkId = frameworkList?.find((item) =>
    item.name?.toLowerCase().includes("csrd")
  )?.id;

  useEffect(() => {
    if (frameworkId && assessmentYear && selectedCountry) {
      dispatch(
        getStandardsQuestionnaire({
          id: frameworkId,
          assessment_year: parseInt(assessmentYear, 10),
          country: selectedCountry,
        })
      );

      dispatch(
        getStandardsQuestionnaireAnswers({
          id: frameworkId,
          assessment_year: parseInt(assessmentYear, 10),
          country: selectedCountry,
        })
      );
    }
  }, [
    frameworkId,
    assessmentYear,
    selectedCountry,
    location.pathname,
    dispatch,
  ]);

  const frameworkName = frameworkList?.find((item) =>
    item.name?.toLowerCase().includes("csrd")
  )?.name;
  const handleNext = () => {
    if (currentPage === 1) {
      setCurrentPage((prev) => prev + 1);
    } else if (currentPage === 2) {
      const answer_list = collectAnswers();

      const frameworkId = frameworkList?.find(
        (item) => item.name === frameworkName
      )?.id;

      const payload = {
        assessment_year: parseInt(assessmentYear, 10),
        country: selectedCountry,
        answer_list: answer_list,
      };

      dispatch(
        saveStandardsQuestionnaire({
          id: frameworkId,
          payload,
        })
      );
    }
  };

  const splitSection =
    "Double Materiality Assessment Questionnaire for Stakeholders";
  const mainSections = Object.keys(questionDict);
  const splitIndex = mainSections.indexOf(splitSection);

  const page1Sections = mainSections.slice(0, splitIndex);
  const page2Sections = mainSections.slice(splitIndex);
  const currentSections = currentPage === 1 ? page1Sections : page2Sections;

  const requiresExplainMulti = (opt) => {
    const v = String(opt || "").toLowerCase();
    return (
      v.includes("please specify") ||
      v.includes("explain below") ||
      v.includes("other entities")
    );
  };

  const isCommitteesOptions = (opts) =>
    Array.isArray(opts) &&
    opts.length === 4 &&
    [
      "Audit Committee: ",
      "Remuneration Committee: ",
      "Nomination Committee: ",
      "Other (Please specify):",
    ].every((lbl) =>
      opts.some((o) => o.trim().toLowerCase() === lbl.trim().toLowerCase())
    );

  const countProgressForPage1 = () => {
    let total = 0;
    let answered = 0;

    page1Sections.forEach((mainSection) => {
      Object.keys(questionDict[mainSection] || {}).forEach((sectionName) => {
        const subSections = questionDict[mainSection][sectionName] || {};
        Object.keys(subSections).forEach((subSectionName) => {
          const list = subSections[subSectionName] || [];

          list.forEach((q, qIdx) => {
            const isSub =
              (q.question?.toLowerCase().startsWith("if yes") ||
                (q.question_id && q.question_id !== "None")) ??
              false;

            const parentId =
              q.question_id !== "None" ? q.question_id : list[qIdx - 1]?.id;

            let isYesSelected = false;
            if (isSub && parentId) {
              const parent = list.find((x) => x.id === parentId);
              if (parent?.question_type === "multiselect_check_box") {
                isYesSelected =
                  Array.isArray(selectedValues[parentId]) &&
                  selectedValues[parentId].length > 0;
              } else if (
                parent?.question_type === "choose_from_option" ||
                parent?.question_type === "Choose from Options"
              ) {
                const val = (selectedValues[parentId] || "")
                  .toString()
                  .trim()
                  .toLowerCase();
                isYesSelected = val.startsWith("yes");
              } else {
                const val = (selectedValues[parentId] || "")
                  .toString()
                  .trim()
                  .toLowerCase();
                isYesSelected = val === "yes";
              }
            }

            const visible = !isSub || isYesSelected;
            if (!visible) return;

            if (q.question_type === "table" || q.question_type === "slider") {
              return;
            }

            total += 1;

            let isAns = false;
            if (
              q.question_type === "text" ||
              q.question_type === "short_text"
            ) {
              isAns = Boolean((textAnswers[q.id] || "").toString().trim());
            } else if (q.question_type === "choose_from_option") {
              const val = (selectedValues[q.id] || "").toString().trim();
              if (val) {
                const low = val.toLowerCase();
                if (
                  low.startsWith("yes (please specify") ||
                  low.startsWith("yes, please specify") ||
                  low.startsWith("other")
                ) {
                  isAns = Boolean(
                    (textAnswers[`specify-${q.id}`] || "").toString().trim()
                  );
                } else {
                  isAns = true;
                }
              }
            } else if (q.question_type === "multiselect_check_box") {
              const arr = selectedValues[q.id] || [];
              if (Array.isArray(arr) && arr.length > 0) {
                const opts = q.options || [];
                if (isCommitteesOptions(opts)) {
                  isAns = true;
                } else if (arr.some(requiresExplainMulti)) {
                  isAns = Boolean(
                    (textAnswers[`other-${q.id}`] || "").toString().trim()
                  );
                } else {
                  isAns = true;
                }
              }
            } else {
              isAns = true;
            }

            if (isAns) answered += 1;
          });
        });
      });
    });

    return { total, answered };
  };

  const { total, answered } = countProgressForPage1();
  const progressPercentage = total ? Math.round((answered / total) * 100) : 0;

  const renderOptions = (question) => {
    const isSingleSelect =
      question.question_type === "choose_from_option" ||
      question.question_type === "Choose from Options";
    const isMultiSelect = question.question_type === "multiselect_check_box";

    if (isSingleSelect) {
      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: (() => {
              const optionCount = question.options.length;
              if (optionCount === 4) return "repeat(2, 1fr)"; // 2x2 layout
              if (optionCount === 5) return "repeat(3, 1fr)"; // 3x2 layout
              return question.options.some((o) => o.split(" ").length > 5)
                ? "repeat(2, 1fr)"
                : "repeat(4, 1fr)";
            })(),

            gap: "16px",
          }}
        >
          {question.options.map((option, idx) => (
            <React.Fragment key={idx}>
              <FormControlLabel
                control={
                  <Checkbox
                    className={classes.selectCheckbox}
                    checked={selectedValues?.[question.id] === option}
                    onChange={() => handleSingleSelect(question.id, option)}
                    sx={{
                      color: "#5A404E !important",
                      "&.Mui-checked": { color: "#8C2148 !important" },
                    }}
                  />
                }
                label={option}
              />
              {((typeof selectedValues[question.id] === "string" &&
                selectedValues[question.id]
                  .trim()
                  .toLowerCase()
                  .includes("other (please specify") &&
                option
                  .trim()
                  .toLowerCase()
                  .includes("other (please specify")) ||
                (typeof selectedValues[question.id] === "string" &&
                  (selectedValues[question.id]
                    .trim()
                    .toLowerCase()
                    .startsWith("yes (please specify") ||
                    selectedValues[question.id]
                      .trim()
                      .toLowerCase()
                      .startsWith("yes, please specify")) &&
                  (option
                    .trim()
                    .toLowerCase()
                    .startsWith("yes (please specify") ||
                    option
                      .trim()
                      .toLowerCase()
                      .startsWith("yes, please specify")))) && (
                <Box gridColumn="1 / -1" mt={2}>
                  <TextField
                    variant="outlined"
                    placeholder="Please type your Answer"
                    multiline
                    rows={2}
                    value={textAnswers[`specify-${question.id}`] || ""}
                    onChange={(e) =>
                      handleTextInputChange(
                        `specify-${question.id}`,
                        e.target.value
                      )
                    }
                    className={classes.input}
                  />
                </Box>
              )}
            </React.Fragment>
          ))}
        </div>
      );
    }

    if (isMultiSelect) {
      if (
        JSON.stringify(question.options) ===
        JSON.stringify([
          "Audit Committee: ",
          "Remuneration Committee: ",
          "Nomination Committee: ",
          "Other (Please specify):",
        ])
      ) {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {question.options.map((option, idx) => (
              <React.Fragment key={idx}>
                <FormControlLabel
                  control={
                    <Checkbox
                      className={classes.selectCheckbox}
                      value={option}
                      checked={
                        selectedValues[question.id]?.includes(option) || false
                      }
                      onChange={(e) => handleCheckboxChange(e, question.id)}
                      sx={{
                        color: "#5A404E !important",
                        "&.Mui-checked": { color: "#8C2148 !important" },
                      }}
                    />
                  }
                  label={option}
                />

                {selectedValues[question.id]?.includes(option) && (
                  <Box width="300%" ml={4} mt={1}>
                    <TextField
                      variant="outlined"
                      placeholder={`Enter details for ${option
                        .replace(/:|___/g, "")
                        .trim()}`}
                      multiline
                      rows={2}
                      value={textAnswers[`${question.id}-${option}`] || ""}
                      onChange={(e) =>
                        handleTextInputChange(
                          `${question.id}-${option}`,
                          e.target.value
                        )
                      }
                      className={classes.input}
                    />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </div>
        );
      }

      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: (() => {
              const count = question.options.length;
              if (count === 4) return "repeat(2, 1fr)";
              if (count === 5) return "repeat(3, 1fr)";
              return question.options.some((o) => o.split(" ").length > 6)
                ? "repeat(2, 1fr)"
                : "repeat(4, 1fr)";
            })(),

            gap: "16px",
          }}
        >
          {question.options.map((option, idx) => (
            <React.Fragment key={idx}>
              <FormControlLabel
                control={
                  <Checkbox
                    className={classes.selectCheckbox}
                    value={option}
                    checked={
                      selectedValues[question.id]?.includes(option) || false
                    }
                    onChange={(e) => handleCheckboxChange(e, question.id)}
                    sx={{
                      color: "#5A404E !important",
                      "&.Mui-checked": { color: "#8C2148 !important" },
                    }}
                  />
                }
                label={option}
              />

              {Array.isArray(selectedValues[question.id]) &&
                selectedValues[question.id].some(
                  (val) =>
                    typeof val === "string" &&
                    (val.toLowerCase().includes("please specify") ||
                      val.toLowerCase().includes("explain below") ||
                      val.toLowerCase().includes("other entities"))
                ) &&
                typeof option === "string" &&
                (option.toLowerCase().includes("please specify") ||
                  option.toLowerCase().includes("explain below") ||
                  option.toLowerCase().includes("other entities")) && (
                  <Box gridColumn="1 / -1" mt={2}>
                    <TextField
                      variant="outlined"
                      placeholder="Please type your Answer"
                      multiline
                      rows={2}
                      value={textAnswers[`other-${question.id}`] || ""}
                      onChange={(e) =>
                        handleTextInputChange(
                          `other-${question.id}`,
                          e.target.value
                        )
                      }
                      className={classes.input1}
                    />
                  </Box>
                )}
            </React.Fragment>
          ))}
        </div>
      );
    }
  };

  return (
    <div className={classes.hideSaveSubmit}>
      {csrdQuestions.length > 0 && (
        <Grid mb={4}>
          {currentPage === 1 && (
            <Box
              sx={{
                position: "relative",
                mb: -2,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{
                    height: 12,
                    borderRadius: 6,
                    width: 200,
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#32CD32",
                    },
                    backgroundColor: "#e0e0e0",
                  }}
                />
                <Typography fontSize={14} fontWeight={500}>
                  {progressPercentage}%
                </Typography>
              </Box>
            </Box>
          )}

          <div className={classes.questionList}>
            {currentSections.map((mainSection, sectionIdx) => (
              <React.Fragment key={sectionIdx}>
                <div className={classes.scoreDiv}>
                  <Typography
                    fontSize={20}
                    fontWeight={550}
                    paddingTop="10px"
                    sx={{
                      display: "block",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.4,
                      maxWidth: "100%",
                    }}
                  >
                    {mainSection}
                  </Typography>
                </div>

                {Object.keys(questionDict[mainSection]).map(
                  (sectionName, sectionIdx) => (
                    <div key={sectionIdx}>
                      {/* SECTION TITLE */}
                      <Typography
                        fontSize={19}
                        fontWeight={700}
                        paddingTop="10px"
                      >
                        {`${toRoman(sectionIdx + 1)}. ${sectionName}`}
                      </Typography>

                      {sectionName === "Environmental Topics" &&
                        hasSliderQuestions && (
                          <Box
                            display="flex"
                            justifyContent="flex-end"
                            pr={6}
                            mb={2}
                            sx={{
                              //   width: "1500px",
                              transform: "scale(1.2)",
                              transformOrigin: "top right",
                            }}
                          >
                            <Box textAlign="center">
                              <Typography
                                fontSize={16}
                                fontWeight={600}
                                mb={1}
                                sx={{
                                  marginLeft: "-165px",
                                }}
                              >
                                Score
                              </Typography>
                              <Box
                                display="flex"
                                gap={5}
                                justifyContent="flex-end"
                              >
                                {["Low", "Medium", "High"].map((label, idx) => (
                                  <Typography
                                    key={idx}
                                    fontSize={14}
                                    fontWeight={600}
                                    sx={{ width: "70px", textAlign: "center" }}
                                  >
                                    {label}
                                  </Typography>
                                ))}
                              </Box>
                              <Box
                                display="flex"
                                gap={1.5}
                                mt={0.5}
                                justifyContent="flex-end"
                              >
                                {[1, 2, 3, 4, 5].map((num) => (
                                  <Typography
                                    key={num}
                                    fontSize={14}
                                    fontWeight={600}
                                    sx={{ width: "43px", textAlign: "center" }}
                                  >
                                    {num}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        )}

                      {Object.keys(questionDict[mainSection][sectionName]).map(
                        (subSectionName, subIdx) => {
                          let questionCounter = 1;
                          return (
                            <div key={subIdx}>
                              <Typography
                                fontSize={17}
                                fontWeight={700}
                                paddingTop="8px"
                                paddingLeft="20px"
                              >
                                {subSectionName}
                              </Typography>

                              {questionDict[mainSection][sectionName][
                                subSectionName
                              ].map((question, qIdx) => {
                                let displayIndex = null;
                                const isSubQuestion =
                                  (question.question
                                    ?.toLowerCase()
                                    ?.startsWith("if yes") ||
                                    (question.question_id &&
                                      question.question_id !== "None")) ??
                                  false;

                                const parentId =
                                  question.question_id !== "None"
                                    ? question.question_id
                                    : questionDict[mainSection][sectionName][
                                        subSectionName
                                      ][qIdx - 1]?.id;

                                const parentQuestion = questionDict[
                                  mainSection
                                ][sectionName][subSectionName]?.find(
                                  (q) => q.id === parentId
                                );

                                let isYesSelected = false;

                                if (
                                  parentQuestion?.question_type ===
                                  "multiselect_check_box"
                                ) {
                                  isYesSelected =
                                    Array.isArray(selectedValues[parentId]) &&
                                    selectedValues[parentId].length > 0;
                                } else if (
                                  parentQuestion?.question_type ===
                                    "choose_from_option" ||
                                  parentQuestion?.question_type ===
                                    "Choose from Options"
                                ) {
                                  const val = (selectedValues[parentId] || "")
                                    .toString()
                                    .trim()
                                    .toLowerCase();
                                  isYesSelected = val.startsWith("yes");
                                } else {
                                  const val = (selectedValues[parentId] || "")
                                    .toString()
                                    .trim()
                                    .toLowerCase();
                                  isYesSelected = val === "yes";
                                }

                                if (isSubQuestion && !isYesSelected)
                                  return null;
                                if (!isSubQuestion)
                                  displayIndex = questionCounter++;

                                return (
                                  <Grid
                                    key={question.id}
                                    className={classes.gridItem}
                                  >
                                    {question.question_type !== "slider" &&
                                      question.question && (
                                        <Typography
                                          fontSize={18}
                                          fontWeight={500}
                                          paddingLeft={4}
                                          paddingBottom={2}
                                        >
                                          {displayIndex !== null &&
                                            `${displayIndex}. `}{" "}
                                          {question.question}
                                        </Typography>
                                      )}

                                    <Box
                                      display="flex"
                                      flexWrap="wrap"
                                      paddingLeft={6}
                                    >
                                      {renderOptions(question)}
                                      {question.question_type === "text" && (
                                        <Box width="100%">
                                          <CeroInput
                                            className={
                                              classes.textAreaContainer
                                            }
                                            rows={2}
                                            placeholder="Please type your Answer"
                                            multiline
                                            value={
                                              textAnswers[question.id] || ""
                                            }
                                            onChange={(e) =>
                                              handleTextInputChange(
                                                question.id,
                                                e.target.value
                                              )
                                            }
                                          />
                                        </Box>
                                      )}
                                      {question.question_type ===
                                        "short_text" &&
                                        (question.question.includes(
                                          "Contact"
                                        ) ? (
                                          <CeroPhoneInput
                                            label="Contact Number"
                                            value={
                                              textAnswers[question.id] || ""
                                            }
                                            onChange={(value) =>
                                              handleTextInputChange(
                                                question.id,
                                                value
                                              )
                                            }
                                            classes={{
                                              container: classes.phoneInput,
                                            }}
                                          />
                                        ) : (
                                          <Box width="50%">
                                            <CeroInput
                                              classes={{
                                                container:
                                                  classes.textAreaContainerShort,
                                                input:
                                                  classes.textAreaContainerInput,
                                              }}
                                              placeholder="Please type your Answer"
                                              multiline
                                              value={
                                                textAnswers[question.id] || ""
                                              }
                                              onChange={(e) =>
                                                handleTextInputChange(
                                                  question.id,
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </Box>
                                        ))}

                                      {question.question_type === "slider" &&
                                        sliderPrefillReady && (
                                          <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            width="100%"
                                            flexWrap="wrap"
                                            mb={3}
                                            sx={{
                                              paddingRight: "40px",
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                flex: 1,
                                                minWidth: "250px",
                                                pr: 4,
                                              }}
                                            >
                                              <Typography
                                                fontSize={16}
                                                fontWeight={500}
                                              >
                                                {displayIndex !== null &&
                                                  `${displayIndex}.`}{" "}
                                                {question.question}
                                              </Typography>
                                            </Box>

                                            <Box
                                              sx={{
                                                width: "258px",
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                mr: "auto",
                                              }}
                                            >
                                              {sliderPrefillReady && (
                                                <Slider
                                                  size="medium"
                                                  value={
                                                    sliderAnswers[
                                                      String(question.id)
                                                    ] ?? 3
                                                  }
                                                  onChange={(e, value) =>
                                                    setSliderAnswers(
                                                      (prev) => ({
                                                        ...prev,
                                                        [String(question.id)]:
                                                          value,
                                                      })
                                                    )
                                                  }
                                                  valueLabelDisplay="auto"
                                                  step={1}
                                                  min={1}
                                                  max={5}
                                                  sx={{
                                                    color: "#8C2148",
                                                    width: "100%",
                                                    "& .MuiSlider-thumb": {
                                                      width: 14,
                                                      height: 14,
                                                    },
                                                    "& .MuiSlider-track": {
                                                      backgroundColor:
                                                        "transparent",
                                                      border: "none",
                                                      height: 5,
                                                    },
                                                    "& .MuiSlider-rail": {
                                                      height: 5,
                                                      backgroundColor:
                                                        "#E0E0E0",
                                                      opacity: 1,
                                                    },
                                                  }}
                                                />
                                              )}
                                            </Box>
                                          </Box>
                                        )}

                                      {question.question_type === "table" &&
                                        (question.condition ===
                                        "csrd_table_1" ? (
                                          <OversightFormTable
                                            oversightFormTable={
                                              oversightFormTable
                                            }
                                            setOversightFormTable={
                                              setOversightFormTable
                                            }
                                          />
                                        ) : question.condition ===
                                          "csrd_table_2" ? (
                                          <DueDiligenceTable
                                            dueDiligenceData={dueDiligenceData}
                                            setDueDiligenceData={
                                              setDueDiligenceData
                                            }
                                          />
                                        ) : question.condition ===
                                          "csrd_table_3" ? (
                                          <PolicyTable
                                            policyData={policyData}
                                            setPolicyData={setPolicyData}
                                          />
                                        ) : question.condition ===
                                          "csrd_table_4" ? (
                                          <ResourceAllocationTable
                                            resourceData={resourceData}
                                            setResourceData={setResourceData}
                                          />
                                        ) : question.condition ===
                                          "csrd_table_5" ? (
                                          <SustainabilityTargetsTable
                                            targetsData={targetsData}
                                            setTargetsData={setTargetsData}
                                          />
                                        ) : null)}
                                    </Box>
                                  </Grid>
                                );
                              })}
                            </div>
                          );
                        }
                      )}
                    </div>
                  )
                )}
              </React.Fragment>
            ))}
          </div>

          <Box display="flex" justifyContent="flex-end" mt={4} gap={2}>
            {currentPage > 1 && (
              <Button
                className={classes.submitbutton}
                variant="outlined"
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </Button>
            )}
            {currentPage < 2 ? (
              <Button
                className={classes.submitbutton}
                variant="contained"
                onClick={handleNext}
              >
                Next
              </Button>
            ) : (
              <Button
                className={classes.submitbutton}
                variant="contained"
                onClick={handleNext}
              >
                Next
              </Button>
            )}
          </Box>
        </Grid>
      )}
    </div>
  );
}

export default CsrdQuestionnaire;
